import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client'
import type {
    HolidayMark,
    MonthlyWorkingTime,
    OvertimeTier,
    WorkingTimeBalance,
    WorkingTimeSettings,
} from '../types/api'

// ---------- Arbeitszeit-Verwaltung (KONZEPT_ARBEITSZEIT.md) ----------

export function workingTimeMonthlyQueryString(
    workspaceId: number,
    userId: number | null,
    year: number,
    month: number,
): string {
    const params = new URLSearchParams({
        workspaceId: String(workspaceId),
        year: String(year),
        month: String(month),
    })
    if (userId !== null) params.set('userId', String(userId))
    return params.toString()
}

export function useWorkingTimeMonthly(
    workspaceId: number | null,
    userId: number | null,
    year: number,
    month: number,
) {
    return useQuery({
        queryKey: ['workingTimeMonthly', workspaceId, userId ?? 'self', year, month],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<MonthlyWorkingTime>(
                `/working-time/monthly?${workingTimeMonthlyQueryString(
                    workspaceId!,
                    userId,
                    year,
                    month,
                )}`,
            ),
    })
}

export function useWorkingTimeBalance(
    workspaceId: number | null,
    userId: number | null,
    year: number,
) {
    const params = new URLSearchParams({
        workspaceId: String(workspaceId),
        year: String(year),
    })
    if (userId !== null) params.set('userId', String(userId))
    return useQuery({
        queryKey: ['workingTimeBalance', workspaceId, userId ?? 'self', year],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<WorkingTimeBalance>(
                `/working-time/balance?${params.toString()}`,
            ),
    })
}

export function useWorkingTimeSettings(workspaceId: number | null) {
    return useQuery({
        queryKey: ['workingTimeSettings', workspaceId],
        enabled: workspaceId !== null,
        queryFn: () =>
            apiRequest<WorkingTimeSettings>(
                `/workspaces/${workspaceId}/working-time-settings`,
            ),
    })
}

/** PUT ersetzt das komplette Settings-Objekt (Backend upsertet mit Defaults). */
export function useWorkingTimeSettingsMutation(workspaceId: number | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (body: WorkingTimeSettings) =>
            apiRequest<WorkingTimeSettings>(
                `/workspaces/${workspaceId}/working-time-settings`,
                { method: 'PUT', body },
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['workingTimeSettings', workspaceId],
            })
            void queryClient.invalidateQueries({
                queryKey: ['workingTimeMonthly', workspaceId],
            })
        },
    })
}

// ---------- Feiertagsmarkierungen (F5) ----------

/** Marks eines Monats (für Zeilen-Aktionen + Holiday-Dialog). */
export function useHolidayMarks(
    workspaceId: number | null,
    userId: number | null,
    year: number,
    month: number,
) {
    const pad = (n: number) => String(n).padStart(2, '0')
    const lastDay = new Date(year, month, 0).getDate()
    const params = new URLSearchParams({
        workspaceId: String(workspaceId),
        from: `${year}-${pad(month)}-01`,
        to: `${year}-${pad(month)}-${pad(lastDay)}`,
    })
    if (userId !== null) params.set('userId', String(userId))
    return useQuery({
        queryKey: ['holidayMarks', workspaceId, userId ?? 'self', year, month],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const res = await apiRequest<{ data: HolidayMark[] }>(
                `/working-time/holiday-marks?${params.toString()}`,
            )
            return res.data
        },
    })
}

/** Feiertag markieren/entfernen — Mitglied selbst oder OWNER/ADMIN. */
export function useHolidayMarkMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({
            queryKey: ['holidayMarks', workspaceId],
        })
        void queryClient.invalidateQueries({
            queryKey: ['workingTimeMonthly', workspaceId],
        })
        void queryClient.invalidateQueries({
            queryKey: ['workingTimeBalance', workspaceId],
        })
    }

    const upsert = useMutation({
        mutationFn: (body: {
            date: string
            factor?: number | null
            note?: string | null
            userId?: number
        }) =>
            apiRequest<HolidayMark>('/working-time/holiday-marks', {
                method: 'PUT',
                body: { workspaceId, ...body },
            }),
        onSuccess: invalidate,
    })

    const remove = useMutation({
        mutationFn: ({ date, userId }: { date: string; userId?: number }) => {
            const params = new URLSearchParams({
                workspaceId: String(workspaceId),
                date,
            })
            if (userId !== undefined) params.set('userId', String(userId))
            return apiRequest<void>(
                `/working-time/holiday-marks?${params.toString()}`,
                { method: 'DELETE' },
            )
        },
        onSuccess: invalidate,
    })

    return { upsert, remove }
}

// ---------- Überstunden-Staffel (F4) ----------

export function useOvertimeTiers(workspaceId: number | null) {
    return useQuery({
        queryKey: ['overtimeTiers', workspaceId],
        enabled: workspaceId !== null,
        queryFn: async () => {
            const res = await apiRequest<{ data: OvertimeTier[] }>(
                `/workspaces/${workspaceId}/overtime-tiers`,
            )
            return res.data
        },
    })
}

/** PUT ersetzt das komplette Set (strikt aufsteigende fromHours, Backend prüft). */
export function useOvertimeTiersMutation(workspaceId: number | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (tiers: OvertimeTier[]) =>
            apiRequest<{ data: OvertimeTier[] }>(
                `/workspaces/${workspaceId}/overtime-tiers`,
                { method: 'PUT', body: { tiers } },
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['overtimeTiers', workspaceId],
            })
            void queryClient.invalidateQueries({
                queryKey: ['workingTimeMonthly', workspaceId],
            })
            void queryClient.invalidateQueries({
                queryKey: ['workingTimeBalance', workspaceId],
            })
        },
    })
}

// ---------- Monatsabschluss (Phase 4, Konzept F7/F8) ----------

/** Monat abschließen (Snapshot) / entsperren — selbst oder OWNER/ADMIN
 * (Konzept F9: userId entfällt = eigenes Konto). */
export function useCloseMonthMutations(workspaceId: number | null) {
    const queryClient = useQueryClient()
    const invalidate = () => {
        void queryClient.invalidateQueries({
            queryKey: ['workingTimeMonthly', workspaceId],
        })
        void queryClient.invalidateQueries({
            queryKey: ['workingTimeBalance', workspaceId],
        })
    }

    const close = useMutation({
        mutationFn: (body: { userId?: number; year: number; month: number }) =>
            apiRequest<{ monthClosed: boolean; closedAt: string; days: number }>(
                '/working-time/close-month',
                { method: 'POST', body: { workspaceId, ...body } },
            ),
        onSuccess: invalidate,
    })

    const reopen = useMutation({
        mutationFn: (body: { userId?: number; year: number; month: number }) =>
            apiRequest<{ monthClosed: boolean; removedDays: number }>(
                '/working-time/reopen-month',
                { method: 'POST', body: { workspaceId, ...body } },
            ),
        onSuccess: invalidate,
    })

    return { close, reopen }
}
